import {
  AbsoluteCenter,
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  FormControl,
  FormLabel,
  Image,
  ListItem,
  Stack,
  Text,
  UnorderedList,
} from '@chakra-ui/react';
import { Form, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';

import {
  AnimatedLogo,
  CustomDrawerClosePrimaryButton,
  CustomFileInput,
  CustomFormErrorMessage,
  PrimaryButton,
  StatusBox,
} from '../../../components/custom';
import { MinusIcon, RoundedPlusIcon } from '../../../components/icons';
import { fileValidation } from '../../../constants/validationConstants';
import { convertFileToBase64 } from '../../../helpers/utilityFunctions';
import useGetUIErrorMessage from '../../../hooks/useGetUIErrorMessage';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { getDocumentRequestV2Async } from '../../../store/slices/additionalDocument/getDocumentRequestSliceV2';
import {
  resetUploadAdditionalDocuments,
  uploadAdditionalDocumentsAsync,
} from '../../../store/slices/additionalDocument/uploadAdditionalDocumentsSlice';

interface AdditionalDocumentsFormProps {
  documentRequestId: string;
  documentArray: {
    name: string;
    templateUrl: string;
  }[];
}

export default function AdditionalDocumentsForm({
  documentRequestId,
  documentArray,
}: AdditionalDocumentsFormProps) {
  const dispatch = useAppDispatch();

  const { status, value } = useAppSelector(
    (state) => state.uploadAdditionalDocuments,
  );

  const errorMessage = useGetUIErrorMessage({
    message: value?.message,
    errors: value?.errors,
    customMessage:
      'An error occurred while submitting addition documents, try again.',
  });

  const initialValues: { [key: string]: File | null } = documentArray.reduce(
    (a, b) => ({ ...a, [b.name]: null }),
    {},
  );

  const validationSchema = Yup.object().shape(
    documentArray.reduce(
      (accumulator, documentObject) => ({
        ...accumulator,
        [documentObject.name]: fileValidation.required(
          `${documentObject.name} is required`,
        ),
      }),
      {},
    ),
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      const documentsInBase64 = await Promise.all(
        Object.keys(values).map(async (key, index) => ({
          name: documentArray[index],
          file: values[key] ? await convertFileToBase64(values[key]) : '',
        })),
      );

      dispatch(
        uploadAdditionalDocumentsAsync({
          documentRequestId,
          documents: documentsInBase64?.map(({ name, file }) => ({
            name: name.name,
            file,
          })),
        }),
      );
    },
  });

  const {
    errors,
    touched,
    values,
    isValid,
    setFieldValue,
    setFieldTouched,
    handleSubmit,
  } = formik;

  const handleFileUploadChange = (fieldName: string, file: File) => {
    setFieldValue(fieldName, file);
  };

  if (status === 'loading') {
    return (
      <Box
        position="relative"
        zIndex="2"
        height="80vh"
        top="-120px"
        background="#fff"
      >
        <AbsoluteCenter>
          <AnimatedLogo />
        </AbsoluteCenter>
      </Box>
    );
  }

  if (status === 'success') {
    return (
      <>
        <AbsoluteCenter axis="vertical" top="45%" left="0" right="0">
          <StatusBox
            variant="success"
            title="Documents Submitted Successfully!"
          >
            {value?.message ?? ''}
          </StatusBox>
        </AbsoluteCenter>

        <CustomDrawerClosePrimaryButton
          onClick={() => dispatch(getDocumentRequestV2Async())}
        >
          Done
        </CustomDrawerClosePrimaryButton>
      </>
    );
  }

  if (status === 'failed') {
    return (
      <>
        <Box
          position="relative"
          zIndex="2"
          height="80vh"
          top="-120px"
          background="#fff"
        >
          <AbsoluteCenter axis="vertical" top="45%" left="0" right="0">
            <StatusBox variant="error" title="Failed to upload documents">
              {errorMessage}
            </StatusBox>
          </AbsoluteCenter>
        </Box>
        {/* <CustomDrawerFooter> */}
        <PrimaryButton
          onClick={() => {
            dispatch(resetUploadAdditionalDocuments());
          }}
          w="full"
        >
          Retry
        </PrimaryButton>
        {/* </CustomDrawerFooter> */}
      </>
    );
  }

  return (
    <>
      <Box mt={4} mb={6}>
        <Text color="#000" mb={1}>
          Additional information required
        </Text>
        <Text color="gray.300" fontSize=".875rem" lineHeight="171.5%">
          Please provide the necessary information and upload the required
          documents to proceed.
        </Text>
      </Box>

      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <Box display="flex" flexDirection="column">
            <Stack spacing={8} flex="1">
              {documentArray.map((documentObject, index) => (
                <Stack key={documentObject.name} spacing={2}>
                  <FormControl
                    key={documentObject.name + index}
                    id={documentObject.name + index}
                    isInvalid={Boolean(
                      touched[documentObject.name] &&
                      errors[documentObject.name],
                    )}
                  >
                    <FormLabel
                      style={{
                        textTransform: 'capitalize',
                      }}
                    >
                      {documentObject.name}
                    </FormLabel>
                    <CustomFileInput
                      onChange={(value) =>
                        handleFileUploadChange(documentObject.name, value)
                      }
                      fieldName={documentObject.name}
                      isInvalid={Boolean(
                        touched[documentObject.name] &&
                        errors[documentObject.name],
                      )}
                      value={values[documentObject.name]}
                      setFieldTouched={setFieldTouched}
                      accept=".png, .jpg, .jpeg, .pdf"
                    />
                    <CustomFormErrorMessage>
                      {touched[documentObject.name] &&
                        errors[documentObject.name]}
                    </CustomFormErrorMessage>
                  </FormControl>

                  {documentObject?.name?.toLowerCase()?.includes('address') &&
                    documentObject?.templateUrl === '' && (
                      <Box
                        background="ash.100"
                        border="1px solid #ebedef"
                        borderRadius="8px"
                        padding="12px 16px"
                        fontSize=".8125rem"
                        lineHeight="154%"
                        letterSpacing="-0.12px"
                        color="gray.200"
                      >
                        <Text fontWeight="500">
                          Proof of address can be any of these documents:
                        </Text>
                        <UnorderedList>
                          <ListItem>
                            Utility bill for services to the address.
                          </ListItem>
                          <ListItem>
                            Bank statement showing current address.
                          </ListItem>
                          <ListItem>Tax assessment.</ListItem>
                        </UnorderedList>
                      </Box>
                    )}

                  {typeof documentObject.templateUrl === 'string' &&
                    documentObject.templateUrl.trim() !== '' && (
                      <Accordion allowToggle>
                        <AccordionItem
                          style={{
                            border: 'none',
                            background: '#FAFAFA',
                            borderRadius: '12px',
                          }}
                        >
                          {({ isExpanded }) => (
                            <>
                              <h2>
                                <AccordionButton
                                  _hover={{
                                    background: '#F5F6F6',
                                    borderRadius: '12px',
                                  }}
                                >
                                  <Box
                                    as="span"
                                    flex="1"
                                    textAlign="left"
                                    fontSize="14px"
                                  >
                                    View {documentObject.name} template
                                  </Box>
                                  {isExpanded ? (
                                    <MinusIcon />
                                  ) : (
                                    <RoundedPlusIcon />
                                  )}
                                </AccordionButton>
                              </h2>

                              <AccordionPanel pb={4}>
                                <Image
                                  src={documentObject.templateUrl}
                                  alt=""
                                  mb={2}
                                  mx="auto"
                                />
                              </AccordionPanel>
                            </>
                          )}
                        </AccordionItem>
                      </Accordion>
                    )}
                </Stack>
              ))}
            </Stack>

            {/* <CustomDrawerFooter> */}
            <PrimaryButton mt={8} type="submit" isDisabled={!isValid} w="full">
              Save and Continue
            </PrimaryButton>
            {/* </CustomDrawerFooter> */}
          </Box>
        </Form>
      </FormikProvider>
    </>
  );
}
